import React, { useRef } from "react";
import {
    motion,
    useScroll,
    useTransform,
    useReducedMotion,
    useSpring,
} from "framer-motion";
import { FadeUp, MaskText } from "@/components/Reveal";
import { PHILOSOPHY } from "@/data/content";

export const About = () => {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    // Smoothed section progress for orb parallax (background only)
    const sectionProgress = useSpring(scrollYProgress, {
        stiffness: 90,
        damping: 26,
        mass: 0.4,
    });
    const orbX1 = useTransform(sectionProgress, [0, 1], [-30, 30]);
    const orbY1 = useTransform(sectionProgress, [0, 1], [-40, 90]);
    const orbX2 = useTransform(sectionProgress, [0, 1], [20, -30]);
    const orbY2 = useTransform(sectionProgress, [0, 1], [40, -120]);

    return (
        <section
            id="about"
            ref={ref}
            data-testid="about-section"
            className="relative py-28 sm:py-36 lg:py-40 bg-[hsl(var(--bg-base))] overflow-hidden"
        >
            {/* Scroll-tracked ambient orbs */}
            <motion.div
                aria-hidden
                style={{ x: orbX1, y: orbY1 }}
                className="absolute -left-40 top-1/3 w-[680px] h-[680px] rounded-full pointer-events-none"
            >
                <div
                    className="absolute inset-0 rounded-full"
                    style={{
                        background:
                            "radial-gradient(circle, hsla(176,62%,44%,0.16), transparent 60%)",
                        filter: "blur(40px)",
                    }}
                />
            </motion.div>
            <motion.div
                aria-hidden
                style={{ x: orbX2, y: orbY2 }}
                className="absolute -right-40 top-10 w-[600px] h-[600px] rounded-full pointer-events-none"
            >
                <div
                    className="absolute inset-0 rounded-full"
                    style={{
                        background:
                            "radial-gradient(circle, hsla(38,92%,62%,0.12), transparent 60%)",
                        filter: "blur(40px)",
                    }}
                />
            </motion.div>

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                <div className="grid grid-cols-12 gap-8 lg:gap-12">
                    <div className="col-span-12 md:col-span-3 lg:col-span-3">
                        <FadeUp>
                            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--text-muted))]">
                                <span className="text-[hsl(var(--accent-gold))]">❖</span> 01 /
                                Philosophy
                            </div>
                        </FadeUp>
                    </div>
                    <div
                        className="col-span-12 md:col-span-9 lg:col-span-9"
                        data-testid="about-manifesto"
                    >
                        {/* Title: per-line mask reveal, settles BEFORE reaching top */}
                        <MaskText
                            as="h2"
                            testId="about-headline"
                            lines={PHILOSOPHY}
                            className="font-display text-[clamp(2rem,5.5vw,4.6rem)] leading-[0.96] tracking-tight"
                        />

                        <div className="grid sm:grid-cols-2 gap-8 mt-16">
                            <FadeUp delay={0.05}>
                                <p className="text-[hsl(var(--text-secondary))] text-base leading-relaxed">
                                    Studio Shakthi is a content creation company specializing in
                                    the entertainment space. We develop, produce, finish and
                                    deliver — feature films, web series and platform-native
                                    originals — with a single obsession: stories that travel.
                                </p>
                            </FadeUp>
                            <FadeUp delay={0.08}>
                                <p className="text-[hsl(var(--text-secondary))] text-base leading-relaxed">
                                    Our work sits at the intersection of craft and global
                                    distribution. We treat storytelling as engineering: a system
                                    designed to provoke feeling — then scale it.
                                </p>
                            </FadeUp>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
